import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { CreditCard, Wallet, MapPin, Phone, User, Mail, Tag, X, Clock, CheckCircle2, Plus } from 'lucide-react';

export default function Checkout() {
  const { items, subtotal, deliveryFee, tax, total: cartTotal, clearCart, systemConfig } = useCart();
  const { user, authClient, sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', customer_email: '', delivery_address: '', notes: '' });
  const [payment, setPayment] = useState('cod');
  const [placing, setPlacing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [suggestedCoupons, setSuggestedCoupons] = useState([]);
  const [slotMode, setSlotMode] = useState('asap');
  const [scheduledTime, setScheduledTime] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [showAddAddr, setShowAddAddr] = useState(false);
  const [newAddr, setNewAddr] = useState({ label: 'Home', address_line: '', phone: '', is_default: false });

  // Phone number verification states
  const [showPhoneVerifyModal, setShowPhoneVerifyModal] = useState(false);
  const [verifyPhoneNum, setVerifyPhoneNum] = useState('');
  const [verifyOtpVal, setVerifyOtpVal] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyCountdown, setVerifyCountdown] = useState(0);

  const getISTDateString = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utc + (5.5 * 60 * 60000));
    const yyyy = istDate.getFullYear();
    const mm = String(istDate.getMonth() + 1).padStart(2, '0');
    const dd = String(istDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getISTDateDisplay = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utc + (5.5 * 60 * 60000));
    return istDate.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getMinTime = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utc + (5.5 * 60 * 60000) + 30 * 60 * 1000);
    const hh = String(istDate.getHours()).padStart(2, '0');
    const mm = String(istDate.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // If kitchen is closed and pre-orders are active, force scheduled mode
  useEffect(() => {
    if (systemConfig && !systemConfig.is_currently_open && systemConfig.allow_preorders) {
      setSlotMode('scheduled');
    }
  }, [systemConfig]);

  useEffect(() => {
    if (user?.name && !form.customer_name) setForm((f) => ({ ...f, customer_name: user.name }));
    if (user?.email && !form.customer_email) setForm((f) => ({ ...f, customer_email: user.email }));
  }, [user]);  // eslint-disable-next-line

  useEffect(() => {
    if (systemConfig) {
      const codActive = systemConfig.accept_cod ?? true;
      const cfActive = systemConfig.accept_cashfree ?? true;
      if (!codActive && cfActive) {
        setPayment('cashfree');
      } else if (codActive && !cfActive) {
        setPayment('cod');
      }
    }
  }, [systemConfig]);

  useEffect(() => {
    api.get('/coupons').then((r) => setSuggestedCoupons(r.data)).catch(() => {});
    if (user) authClient.get('/addresses').then((r) => {
      setAddresses(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def && !form.delivery_address) setForm((f) => ({ ...f, delivery_address: def.address_line, customer_phone: f.customer_phone || def.phone || '' }));
    }).catch(() => {});
  }, [user]);  // eslint-disable-next-line

  useEffect(() => {
    if (verifyCountdown > 0) {
      const timer = setTimeout(() => setVerifyCountdown(verifyCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [verifyCountdown]);

  // Load and validate sessionStorage coupon on mount / subtotal changes
  useEffect(() => {
    const loadStoredCoupon = async () => {
      try {
        const stored = sessionStorage.getItem('pushpanjali_coupon');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.code) {
            const code = parsed.code.trim().toUpperCase();
            const { data } = await api.post('/coupons/validate', { code, subtotal });
            setCoupon(data);
            setCouponCode(code);
          }
        }
      } catch (e) {
        sessionStorage.removeItem('pushpanjali_coupon');
      }
    };
    if (subtotal > 0) {
      loadStoredCoupon();
    }
  }, [subtotal]);

  // Keep sessionStorage in sync with applied coupon state changes on checkout
  useEffect(() => {
    if (coupon) {
      sessionStorage.setItem('pushpanjali_coupon', JSON.stringify(coupon));
    } else {
      sessionStorage.removeItem('pushpanjali_coupon');
    }
  }, [coupon]);

  useEffect(() => {
    if (user?.phone) {
      setVerifyPhoneNum(user.phone.replace('+91', ''));
    } else if (form.customer_phone) {
      setVerifyPhoneNum(form.customer_phone.replace('+91', ''));
    }
  }, [user, form.customer_phone, showPhoneVerifyModal]);

  if (items.length === 0) {
    const lastId = sessionStorage.getItem('last_placed_order_id');
    if (lastId) {
      sessionStorage.removeItem('last_placed_order_id');
      navigate(`/orders/${lastId}`, { replace: true });
    } else {
      navigate('/menu', { replace: true });
    }
    return null;
  }

  const discount = coupon?.discount || 0;
  const total = Math.max(cartTotal - discount, 0);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post('/coupons/validate', { code: couponCode, subtotal });
      setCoupon(data);
      toast.success(`Coupon applied — you saved ₹${data.discount}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid coupon'); }
  };

  const saveAddress = async () => {
    if (!newAddr.address_line.trim()) return;
    try {
      const { data } = await authClient.post('/addresses', newAddr);
      setAddresses([...addresses, data]);
      setShowAddAddr(false);
      setNewAddr({ label: 'Home', address_line: '', phone: '', is_default: false });
      setForm({ ...form, delivery_address: data.address_line, customer_phone: form.customer_phone || data.phone || '' });
      toast.success('Address saved');
    } catch { toast.error('Failed to save address'); }
  };

  const handleSendVerifyOtp = async () => {
    const cleanPhone = verifyPhoneNum.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setOtpLoading(true);
    try {
      await sendOtp(cleanPhone);
      setOtpSent(true);
      setVerifyCountdown(30);
      toast.success('Verification OTP sent!');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCompletePhoneVerify = async () => {
    const cleanPhone = verifyPhoneNum.replace(/\D/g, '');
    if (verifyOtpVal.length < 6) {
      toast.error('Please enter the OTP');
      return;
    }
    setVerifying(true);
    try {
      await verifyOtp(cleanPhone, verifyOtpVal);
      toast.success('Phone verified successfully!');
      setShowPhoneVerifyModal(false);
      setOtpSent(false);
      setVerifyOtpVal('');
      setForm((f) => ({ ...f, customer_phone: cleanPhone }));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'OTP verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const validate = () => {
    if (!form.customer_name.trim()) return 'Please enter your name';
    if (!/^[6-9]\d{9}$/.test(form.customer_phone.replace(/\s/g, ''))) return 'Enter a valid 10-digit Indian mobile';
    if (form.delivery_address.trim().length < 10) return 'Please enter a full delivery address';
    if (slotMode === 'scheduled' && !scheduledTime) return 'Please pick a scheduled time';
    return null;
  };

  const placeOrder = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setPlacing(true);
    try {
      const orderPayload = {
        ...form,
        items: items.map((i) => ({ item_id: i.item_id, name: i.name, price: i.price, quantity: i.quantity, image_url: i.image_url })),
        payment_method: payment,
        origin_url: window.location.origin,
        coupon_code: coupon?.code || null,
        scheduled_time: slotMode === 'scheduled' ? scheduledTime : null,
      };
      const { data: order } = await authClient.post('/orders', orderPayload);
      sessionStorage.setItem('last_placed_order_id', order.id);
      if (payment === 'cashfree') {
        try {
          const { data: cf } = await api.post('/payments/cashfree/checkout', { order_id: order.id, origin_url: window.location.origin });
          clearCart();
          sessionStorage.removeItem('pushpanjali_coupon');
          const cfInstance = window.Cashfree ? window.Cashfree({ mode: cf.environment }) : null;
          if (cfInstance && cf.payment_session_id) {
            cfInstance.checkout({
              paymentSessionId: cf.payment_session_id,
              redirectTarget: "_self"
            });
          } else {
            window.location.href = cf.checkout_url;
          }
        } catch (paymentErr) {
          clearCart();
          sessionStorage.removeItem('pushpanjali_coupon');
          toast.error('Payment initialization failed. Redirecting to order details...');
          navigate(`/orders/${order.id}`);
        }
      } else {
        clearCart();
        sessionStorage.removeItem('pushpanjali_coupon');
        toast.success('Order placed successfully!');
        navigate(`/orders/${order.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to place order');
      setPlacing(false);
    }
  };

  return (
    <div className="pb-32 md:pb-8 max-w-4xl mx-auto px-4 md:px-6 py-6">
      <h1 className="font-heading font-extrabold text-3xl text-textprimary mb-6">Checkout</h1>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-5">
          {/* Address book for logged-in users */}
          {user && addresses.length > 0 && (
            <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-heading font-bold text-lg text-textprimary">Saved Addresses</h2>
                <button onClick={() => setShowAddAddr(true)} data-testid="add-address-btn" className="text-saffron text-sm font-heading font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> New</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {addresses.map((a) => (
                  <button key={a.id} onClick={() => setForm({ ...form, delivery_address: a.address_line, customer_phone: form.customer_phone || a.phone || '' })} className={`text-left p-3 rounded-xl border-2 transition-all ${form.delivery_address === a.address_line ? 'border-saffron bg-bgsecondary' : 'border-borderbeige'}`} data-testid={`saved-address-${a.id}`}>
                    <div className="font-heading font-semibold text-sm text-textprimary flex items-center gap-2">{a.label}{a.is_default && <span className="text-[10px] bg-golden text-maroon px-1.5 rounded">Default</span>}</div>
                    <div className="text-xs text-textsecondary line-clamp-2 font-body">{a.address_line}</div>
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
            <h2 className="font-heading font-bold text-lg text-textprimary mb-3">Delivery Details</h2>
            <div className="space-y-3">
              <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" /><input data-testid="checkout-name" placeholder="Full name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full pl-10 pr-3 py-2.5 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40" /></div>
              <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" /><input data-testid="checkout-phone" placeholder="10-digit mobile" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="w-full pl-10 pr-3 py-2.5 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40" /></div>
              <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" /><input data-testid="checkout-email" type="email" placeholder="Email (for receipt)" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full pl-10 pr-3 py-2.5 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40" /></div>
              <div className="relative"><MapPin className="absolute left-3 top-3.5 w-4 h-4 text-textlight" /><textarea data-testid="checkout-address" rows={3} placeholder="Full delivery address" value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="w-full pl-10 pr-3 py-2.5 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40" /></div>
              <input data-testid="checkout-notes" placeholder="Delivery notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2.5 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40" />
            </div>
          </section>

          {/* Delivery Slot */}
          <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
            <h2 className="font-heading font-bold text-lg text-textprimary mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-saffron" /> When to deliver?</h2>
            {!systemConfig?.is_currently_open && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 font-body font-semibold">
                ℹ️ Our kitchen is closed right now. You can pre-order for delivery during operational hours.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={() => setSlotMode('asap')} 
                disabled={!systemConfig?.is_currently_open}
                data-testid="slot-asap" 
                className={`p-3 rounded-2xl border-2 text-left disabled:opacity-40 disabled:cursor-not-allowed ${slotMode === 'asap' ? 'border-saffron bg-bgsecondary' : 'border-borderbeige'}`}
              >
                <div className="font-heading font-semibold text-sm">ASAP</div>
                <div className="text-xs text-textsecondary font-body">30-40 min</div>
              </button>
              <button 
                onClick={() => setSlotMode('scheduled')} 
                data-testid="slot-scheduled" 
                className={`p-3 rounded-2xl border-2 text-left ${slotMode === 'scheduled' ? 'border-saffron bg-bgsecondary' : 'border-borderbeige'}`}
              >
                <div className="font-heading font-semibold text-sm">Schedule</div>
                <div className="text-xs text-textsecondary font-body">Pick a time</div>
              </button>
            </div>
            {slotMode === 'scheduled' && (
              <div className="mt-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <span className="block text-xs font-heading font-bold text-textsecondary mb-1">Delivery Date</span>
                    <div className="w-full px-3 py-2.5 border border-borderbeige rounded-xl text-sm bg-bgsecondary text-textsecondary font-medium font-body flex items-center justify-between">
                      <span>Today ({getISTDateDisplay()})</span>
                      <span className="text-[10px] bg-fresh/20 text-fresh px-1.5 py-0.5 rounded font-semibold">Today Only</span>
                    </div>
                  </div>
                  <div className="w-full sm:w-48">
                    <span className="block text-xs font-heading font-bold text-textsecondary mb-1">Choose Time</span>
                    <input 
                      type="time" 
                      data-testid="scheduled-time-input" 
                      value={selectedTime} 
                      onChange={(e) => {
                        const timeVal = e.target.value;
                        setSelectedTime(timeVal);
                        if (timeVal) {
                          const datePart = getISTDateString();
                          setScheduledTime(`${datePart}T${timeVal}:00+05:30`);
                        } else {
                          setScheduledTime('');
                        }
                      }} 
                      min={getMinTime()}
                      className="w-full px-3 py-2.5 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40 font-body" 
                    />
                  </div>
                </div>
                
                {systemConfig?.operating_hours && (
                  <div className="bg-bgsecondary/80 rounded-xl p-3 border border-borderbeige">
                    <span className="block text-xs font-heading font-bold text-textprimary mb-1.5">Weekly Delivery Operating Hours (IST):</span>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-body text-textsecondary">
                      {Object.entries(systemConfig.operating_hours).map(([day, hr]) => (
                        <div key={day} className="flex justify-between py-0.5 border-b border-dashed border-borderbeige last:border-b-0">
                          <span className="capitalize">{day}</span>
                          <span className={hr.is_open ? 'text-maroon font-semibold' : 'text-textlight'}>
                            {hr.is_open ? `${hr.start_time} - ${hr.end_time}` : 'Closed'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Payment */}
          <section className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
            <h2 className="font-heading font-bold text-lg text-textprimary mb-3">Payment Method</h2>
            {(!systemConfig || (systemConfig.accept_cod ?? true) || (systemConfig.accept_cashfree ?? true)) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(!systemConfig || (systemConfig.accept_cod ?? true)) && (
                  <button onClick={() => setPayment('cod')} data-testid="payment-cod" className={`flex items-center gap-3 p-4 rounded-2xl border-2 text-left ${payment === 'cod' ? 'border-saffron bg-bgsecondary' : 'border-borderbeige'}`}>
                    <Wallet className={`w-6 h-6 ${payment === 'cod' ? 'text-saffron' : 'text-textsecondary'}`} />
                    <div><div className="font-heading font-semibold text-sm">Cash on Delivery</div><div className="text-xs text-textsecondary font-body">Pay when it arrives</div></div>
                  </button>
                )}
                {(!systemConfig || (systemConfig.accept_cashfree ?? true)) && (
                  <button onClick={() => setPayment('cashfree')} data-testid="payment-cashfree" className={`flex items-center gap-3 p-4 rounded-2xl border-2 text-left ${payment === 'cashfree' ? 'border-saffron bg-bgsecondary' : 'border-borderbeige'}`}>
                    <CreditCard className={`w-6 h-6 ${payment === 'cashfree' ? 'text-saffron' : 'text-textsecondary'}`} />
                    <div><div className="font-heading font-semibold text-sm">UPI / Card (Cashfree)</div><div className="text-xs text-textsecondary font-body">Fast & Secure</div></div>
                  </button>
                )}
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-sm font-semibold text-red-600">
                ⚠️ No payment methods are currently active. Please contact support.
              </div>
            )}
          </section>
        </div>

        <aside className="md:sticky md:top-24 h-fit bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
          <h2 className="font-heading font-bold text-lg text-textprimary mb-3">Order Summary</h2>
          <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
            {items.map((i) => (
              <div key={i.item_id} className="flex justify-between text-sm font-body">
                <span className="text-textprimary">{i.name} × {i.quantity}</span>
                <span className="text-textsecondary">₹{(i.price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Coupon */}
          <div className="border-t border-borderbeige pt-3 mb-3">
            {coupon ? (
              <div className="bg-fresh/10 text-fresh rounded-xl px-3 py-2 flex justify-between items-center text-sm font-body" data-testid="applied-coupon">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /><b>{coupon.code}</b> saved ₹{coupon.discount}</span>
                <button onClick={() => { setCoupon(null); setCouponCode(''); }} data-testid="remove-coupon"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input" className="w-full min-w-0 px-3 py-2 border border-borderbeige rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-saffron/40" />
                  <button onClick={applyCoupon} data-testid="apply-coupon-btn" className="shrink-0 bg-maroon hover:bg-maroon-dark text-white px-4 py-2 rounded-xl text-sm font-heading font-semibold transition-colors">Apply</button>
                </div>
                {suggestedCoupons.length > 0 && (
                  <div className="mt-2 flex gap-1 overflow-x-auto no-scrollbar">
                    {suggestedCoupons.slice(0, 3).map((c) => (
                      <button key={c.code} onClick={() => { setCouponCode(c.code); }} data-testid={`suggest-coupon-${c.code}`} className="shrink-0 text-[10px] bg-golden/20 text-maroon px-2 py-1 rounded-full font-heading font-semibold flex items-center gap-1"><Tag className="w-3 h-3" />{c.code}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border-t border-borderbeige pt-3 space-y-2 text-sm font-body">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
            {discount > 0 && <div className="flex justify-between text-fresh"><span>Coupon discount</span><span>−₹{discount.toFixed(2)}</span></div>}
            {systemConfig?.show_tax_and_delivery && (
              <>
                <div className="flex justify-between"><span>Delivery</span><span>₹{deliveryFee.toFixed(2)}</span></div>
                <div className="flex justify-between text-textsecondary text-xs"><span>Tax ({systemConfig?.tax_percentage ?? 5}%)</span><span>₹{tax.toFixed(2)}</span></div>
              </>
            )}
            <div className="flex justify-between font-heading font-bold text-lg pt-2 border-t border-borderbeige">
              <span>Total</span><span className="text-maroon" data-testid="checkout-total">₹{total.toFixed(2)}</span>
            </div>
          </div>
          {user && !user.phone_verified && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-xs font-body mb-4">
              <div className="flex gap-2 text-red-800 font-semibold mb-1">
                <span>⚠️ Phone Verification Required</span>
              </div>
              <p className="text-red-700 mb-3">
                Only verified phone numbers can complete orders. Please verify your phone number to proceed.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (user?.phone) setVerifyPhoneNum(user.phone.replace('+91', ''));
                  setShowPhoneVerifyModal(true);
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-xl font-heading font-bold transition-colors"
                data-testid="verify-phone-trigger-btn"
              >
                Verify Phone Number
              </button>
            </div>
          )}

          <button
            onClick={user && !user.phone_verified ? () => toast.error("Please verify your phone number first") : placeOrder}
            disabled={placing || (user && !user.phone_verified) || (systemConfig && !systemConfig.accept_cod && !systemConfig.accept_cashfree)}
            data-testid="place-order-btn"
            className="mt-5 w-full bg-saffron text-white py-3 rounded-2xl font-heading font-bold hover:bg-saffron-dark shadow-warm disabled:opacity-60"
          >
            {placing ? 'Placing order...' : (payment === 'cashfree') ? `Pay ₹${total.toFixed(2)}` : `Place Order`}
          </button>
        </aside>
      </div>

      {/* Phone verification modal */}
      {showPhoneVerifyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full font-body">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-bold text-lg text-textprimary">Verify Phone Number</h3>
              <button onClick={() => { setShowPhoneVerifyModal(false); setOtpSent(false); setVerifyOtpVal(''); }} className="p-1 rounded-full hover:bg-bgsecondary transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {!otpSent ? (
                <>
                  <p className="text-textsecondary text-xs">
                    Enter your mobile number to receive a 6-digit confirmation code.
                  </p>
                  <div>
                    <label className="block text-xs font-semibold text-textsecondary mb-1">PHONE NUMBER</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" />
                      <span className="absolute left-9 top-1/2 -translate-y-1/2 text-sm text-textsecondary font-medium">+91</span>
                      <input
                        type="tel"
                        maxLength={10}
                        placeholder="9999999999"
                        value={verifyPhoneNum}
                        onChange={(e) => setVerifyPhoneNum(e.target.value.replace(/\D/g, ''))}
                        className="w-full pl-20 pr-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-sm text-textprimary transition-colors"
                        data-testid="verify-phone-input"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSendVerifyOtp}
                    disabled={otpLoading}
                    className="w-full bg-saffron text-white py-2.5 rounded-2xl font-heading font-bold shadow-warm flex items-center justify-center gap-2 cursor-pointer mt-4"
                    data-testid="verify-send-otp-btn"
                  >
                    {otpLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Send Verification OTP'
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div className="text-center bg-bgsecondary py-3 px-4 rounded-2xl text-xs text-textsecondary mb-4 flex items-center justify-between">
                    <div className="text-left font-body">
                      <span>OTP sent to <b>+91 {verifyPhoneNum}</b></span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setOtpSent(false); setVerifyOtpVal(''); }}
                      className="text-saffron hover:underline font-semibold text-xs animate-pulse"
                    >
                      Edit
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-textsecondary mb-1">ENTER 6-DIGIT OTP</label>
                    <input
                      type="text"
                      maxLength={6}
                      value={verifyOtpVal}
                      onChange={(e) => setVerifyOtpVal(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="w-full px-4 py-2.5 rounded-2xl bg-white border border-borderbeige focus:outline-none focus:border-saffron text-center text-lg font-heading tracking-widest text-textprimary transition-colors animate-fade-in"
                      data-testid="verify-otp-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleCompletePhoneVerify}
                    disabled={verifying}
                    className="w-full bg-maroon hover:bg-maroon-dark text-white py-3 rounded-2xl font-heading font-semibold shadow-warm transition-colors flex items-center justify-center gap-2 cursor-pointer mt-4"
                    data-testid="verify-otp-submit-btn"
                  >
                    {verifying ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Verify & Proceed'
                    )}
                  </button>
                  <div className="text-center mt-3">
                    {verifyCountdown > 0 ? (
                      <span className="text-xs text-textlight">Resend OTP in {verifyCountdown}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendVerifyOtp}
                        className="text-saffron hover:underline text-xs font-semibold"
                        data-testid="verify-resend-otp-btn"
                      >
                        Resend Code
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New address modal */}
      {showAddAddr && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4"><h3 className="font-heading font-bold text-lg">New Address</h3><button onClick={() => setShowAddAddr(false)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <select value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" data-testid="new-addr-label">
                <option>Home</option><option>Work</option><option>Other</option>
              </select>
              <textarea rows={3} placeholder="Full address" value={newAddr.address_line} onChange={(e) => setNewAddr({ ...newAddr, address_line: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" data-testid="new-addr-line" />
              <input placeholder="Phone (optional)" value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newAddr.is_default} onChange={(e) => setNewAddr({ ...newAddr, is_default: e.target.checked })} /> Set as default</label>
              <button onClick={saveAddress} data-testid="save-address-btn" className="w-full bg-saffron text-white py-2.5 rounded-2xl font-heading font-bold shadow-warm">Save Address</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
